/**
 * State availability only.
 *
 * The backend remains the security authority.
 */
export function controlsFor(state) {
	switch (state) {
		case 'DRAFT':
			return [
				'edit',
				'submit',
				'duplicate',
				'cancel',
				'delete',
			];

		case 'PENDING_APPROVAL':
			return [
				'approve',
				'reject',
				'duplicate',
				'cancel',
				'delete',
			];

		case 'SCHEDULED':
			return [
				'start',
				'duplicate',
				'cancel',
				'delete',
			];

		case 'PLANNING':
			return [];

		case 'RUNNING':
			return [
				'pause',
				'duplicate',
				'cancel',
			];

		case 'PAUSED':
			return [
				'resume',
				'reschedule',
				'duplicate',
				'cancel',
			];

		case 'COMPLETED':
			return [
				'resend',
				'duplicate',
			];

		case 'FAILED_TO_PLAN':
			return [
				'duplicate',
				'cancel',
				'delete',
			];

		case 'CANCELLED':
			return [
				'duplicate',
				'delete',
			];

		default:
			return [];
	}
}

/**
 * Frontend convenience only.
 *
 * The backend repeats these checks and additionally examines recipient
 * activity before allowing deletion.
 */
export function canDeleteCampaign(
	campaign,
) {
	if (
		!campaign ||
		campaign.deletedAt ||
		![
			'DRAFT',
			'PENDING_APPROVAL',
			'SCHEDULED',
			'FAILED_TO_PLAN',
			'CANCELLED',
		].includes(campaign.state)
	) {
		return false;
	}

	if (
		campaign.plan?.plannedAt ||
		campaign.plan?.rescheduledAt ||
		campaign.plan?.rebalancedAt
	) {
		return false;
	}

	const startedStates = [
		'RUNNING',
		'PAUSED',
		'COMPLETED',
	];

	if (
		campaign.stateHistory != null &&
		!Array.isArray(
			campaign.stateHistory,
		)
	) {
		return false;
	}

	if (
		(
			campaign.stateHistory ?? []
		).some(
			(entry) =>
				startedStates.includes(
					entry?.to,
				) ||
				startedStates.includes(
					entry?.from,
				),
		)
	) {
		return false;
	}

	return ![
		'submitted',
		'sent',
		'delivered',
		'read',
		'replied',
		'failed',
		'undelivered',
	].some(
		(key) =>
			campaign.counters?.[key] != null &&
			campaign.counters[key] !== 0,
	);
}

export function availableControls(
	campaign,
) {
	if (campaign?.deletedAt) {
		return [];
	}

	let actions = controlsFor(
		campaign?.state,
	);

	if (!canDeleteCampaign(campaign)) {
		actions = actions.filter(
			(action) =>
				action !== 'delete',
		);
	}

	if (
		Array.isArray(
			campaign?.allowedActions,
		)
	) {
		actions = actions.filter(
			(action) =>
				campaign.allowedActions.includes(
					action,
				),
		);
	}

	if (campaign?.emergencyStopped) {
		actions = actions.filter(
			(action) =>
				![
					'start',
					'resume',
				].includes(action),
		);
	}

	return actions;
}

export default controlsFor;